import {useEffect, useRef} from "react";
import {Skeleton} from "@mantine/core";
import {useChecklistData} from "./ChecklistDataContext";
import {renderElement} from "./renderElement";
import {CheckItemParent} from "./components/CheckItemParent";
import {CheckItemLeafNode} from "./components/CheckItemLeafNode";
import {CheckItemIngress} from "./components/CheckItemIngress";
import {CheckItemDescription} from "./components/CheckItemDescription";
import {DocumentGeneratorRef} from "./components/DocumentGeneratorRef";
import {FileRef} from "./components/FileRef";
import {ChecklistItemType} from "../types";
import {getStatusNames} from "./statusNames";
import styles from "./checklist.module.css";

export const CheckItem = ({
    itemId,
    parentId,
    isLoading,
}: {
    itemId: string;
    parentId: string;
    isLoading?: boolean;
}) => {
    const {
        viewFilteredToc,
        isHidden,
        isSearchHit,
        itemsState,
        onClickItem,
        observeChecklistItemRef,
        unobserveChecklistItemRef,
    } = useChecklistData();

    const itemRef = useRef<HTMLDivElement>(null);
    const node = viewFilteredToc[itemId];

    const itemType = node?.checkItemType;
    const isParent = itemType === ChecklistItemType.CheckItemParent;
    const isOpen = isParent ? (itemsState.openItems[itemId] ?? false) : true;
    const hasChildren = (node?.children?.length ?? 0) > 0;

    useEffect(() => {
        const el = itemRef.current;
        if (el) {
            if (isOpen && hasChildren) {
                observeChecklistItemRef(el);
            } else {
                unobserveChecklistItemRef(el);
            }
        }
        return () => {
            if (el) {
                unobserveChecklistItemRef(el);
            }
        };
        // Callbacks are stable by design (useIntersectionObserver uses refs internally).
        // Including them would cause a render loop since they get new references each render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, hasChildren]);

    if (!node) return null;

    const currentIsLoading =
        isLoading || (itemsState.loadingState[itemId]?.isLoading ?? false);
    const hasElements =
        (node.ingress && itemsState.elements[node.ingress]) ||
        (node.description && itemsState.elements[node.description]) ||
        (node.law && itemsState.elements[node.law]);

    const renderContentSections = () => {
        if (!isOpen) return null;
        if (!node.ingress && !node.description && !node.law) return null;

        if (currentIsLoading && !hasElements) {
            return (
                <div className={styles.contentSections}>
                    <div className={styles.loadingIndicator}>
                        <Skeleton height={12} mb="sm" />
                        <Skeleton height={12} mb="sm" />
                        <Skeleton height={12} width="70%" />
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.contentSections} data-searchable="true">
                {node.ingress &&
                    itemsState.elements[node.ingress] && (
                        <CheckItemIngress ingress={node.ingress} />
                    )}
                {node.law &&
                    itemsState.elements[node.law] && (
                        <div className={styles.contentBody}>
                            <div className={styles.lawSection}>
                                {renderElement(
                                    node.law,
                                    itemsState.elements,
                                    itemsState.objects,
                                )}
                            </div>
                        </div>
                    )}
                {node.description &&
                    itemsState.elements[node.description] && (
                        <CheckItemDescription
                            description={node.description}
                            forceExpanded={isSearchHit(itemId)}
                        />
                    )}
            </div>
        );
    };

    const renderChildren = () => {
        if (!hasChildren || !isOpen || !isParent) return null;

        const visibleChildren = node.children!.filter(
            (childId) => !isHidden(childId),
        );

        return (
            <div data-pw="check-item-children">
                {visibleChildren.map((childId) => (
                    <CheckItem
                        key={childId}
                        itemId={childId}
                        parentId={itemId}
                        isLoading={currentIsLoading}
                    />
                ))}
            </div>
        );
    };

    const renderRelatedItems = () => {
        if (!isOpen || !node.related || node.related.length === 0) return null;

        return (
            <div className={styles.relatedSection}>
                <ul>
                    {node.related.map((rel) => (
                        <DocumentGeneratorRef
                            key={rel.topicId}
                            relatedItem={rel}
                        />
                    ))}
                </ul>
            </div>
        );
    };

    const renderFilesItems = () => {
        if (!isOpen || !node.files || node.files.length === 0) return null;

        return (
            <div className={styles.filesSection}>
                <ul>
                    {node.files.map((file) => {
                        const relatedItem = node.related?.find(
                            (r) => r.topicId === file.topicId,
                        );
                        return (
                            <FileRef
                                key={file.id}
                                fileItem={file}
                                relatedItem={relatedItem}
                            />
                        );
                    })}
                </ul>
            </div>
        );
    };

    const handleClick = () => onClickItem(itemId, parentId);

    if (isParent) {
        return (
            <CheckItemParent
                ref={itemRef}
                itemId={itemId}
                isOpen={isOpen}
                onTitleClick={handleClick}
                names={getStatusNames()}
            >
                {isOpen && (
                    <div className={styles.checkItemContent}>
                        {renderContentSections()}
                        {renderRelatedItems()}
                        {renderFilesItems()}
                        {renderChildren()}
                    </div>
                )}
            </CheckItemParent>
        );
    }

    return (
        <CheckItemLeafNode itemId={itemId} names={getStatusNames()}>
            <div className={styles.checkItemContent}>
                {renderContentSections()}
                {renderRelatedItems()}
                {renderFilesItems()}
            </div>
        </CheckItemLeafNode>
    );
};
